import { WithTheme } from '@issuemanager/apps/client/theme';
import { MenuItem, Typography } from '@mui/material';
import { styled } from '@mui/system';
import React, { useCallback } from 'react';
import { Controller, FieldValues, UseControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TextField, TextFieldProps } from '../text-field';

type SelectOption = {
  value: string;
  label: string;
};

interface ControlledTextFieldProps<TFieldValues extends FieldValues>
  extends Pick<
      TextFieldProps,
      | 'label'
      | 'minRows'
      | 'multiline'
      | 'onChange'
      | 'helperText'
      | 'disabled'
      | 'data-testid'
      | 'labelInfo'
      | 'placeholder'
      | 'inputProps'
    >,
    UseControllerProps<TFieldValues> {
  options?: SelectOption[];
  maxLength?: number;
  isVisible?: boolean;
}

export const StyledMenuItem = styled(MenuItem)<WithTheme<TextFieldProps>>(
  ({ theme }) => ({
    color: theme.tokens.semantic.color.text.muted.value,
  }),
);

export function ControlledTextField<TFieldValues extends FieldValues>({
  name,
  control,
  rules,
  label,
  labelInfo,
  options,
  multiline,
  minRows,
  placeholder,
  helperText,
  onChange: onChangeProp,
  disabled,
  maxLength,
  isVisible = true,
  ...props
}: ControlledTextFieldProps<TFieldValues>): React.JSX.Element | null {
  const { t } = useTranslation('common');
  const generateSelectOptions = useCallback(() => {
    if (!options) return [];
    return options.map((option) => (
      <MenuItem key={option.value} value={option.value}>
        {option.label}
      </MenuItem>
    ));
  }, [options]);

  if (!isVisible) return null;

  return (
    <Controller<TFieldValues>
      control={control}
      rules={rules}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => {
        const testId = props['data-testid'] ?? name;

        const maxLengthLabel =
          multiline && maxLength
            ? `${value?.length ?? 0}/${maxLength}`
            : undefined;

        return (
          <TextField
            select={!!options}
            aria-labelledby={`${name}-label`}
            data-testid={testId}
            label={label}
            placeholder={placeholder}
            labelInfo={labelInfo}
            onChange={(e) => {
              onChange(e.target.value);
              onChangeProp?.(e);
            }}
            onBlur={(e) => {
              const trimmedValue = e.target.value.trim();
              if (trimmedValue !== e.target.value) {
                onChange(trimmedValue);
              }
            }}
            value={options?.length ? value ?? '' : value}
            required={props.inputProps?.required || !!rules?.required}
            error={!!error?.message}
            helperText={helperText ?? error?.message ?? maxLengthLabel}
            minRows={minRows}
            multiline={multiline}
            inputProps={{
              ...props.inputProps,
              'data-testid': `${testId}-input`,
              'aria-readonly': props.inputProps?.readOnly,
              maxLength,
            }}
            disabled={disabled}
            SelectProps={{
              displayEmpty: true,
              renderValue: (v) => {
                const inputValue = v as string;
                if (inputValue === '') {
                  return (
                    <Typography color='#676767'>
                      {t('form.placeholder.common') as string}
                    </Typography>
                  );
                }
                const item = options?.find(
                  (option: SelectOption) => option.value === inputValue,
                );
                return item?.label;
              },
            }}
          >
            {generateSelectOptions()}
          </TextField>
        );
      }}
    />
  );
}
